export const MAX_ASSIGNED_LOGOS = 10;
export const LOGOS_PER_ROW = 5;

export const LOGO_SIZE_PRESETS = {
  small: 60,
  medium: 80,
  large: 100,
};

export const LOGO_POSITION_PRESETS = {
  horizontal: {
    left: -40,
    center: 0,
    right: 40,
  },
  vertical: {
    top: -40,
    middle: 0,
    bottom: 40,
  },
};

const defaultLogo = {
  key: "",
  thumbnail: "",
  url: "",
  scale: LOGO_SIZE_PRESETS.medium,
  title: "",
  offset_x: 0,
  offset_y: 0,
};

export const logoHasAsset = (logo) => Boolean(logo?.thumbnail || logo?.url);

export const getAssignedLogoEntries = (items = []) =>
  items
    .map((item, itemIndex) => ({
      item,
      itemIndex,
      logo: item?.logo,
    }))
    .filter(({ logo }) => logoHasAsset(logo))
    .sort((a, b) => {
      const rowDifference = (a.logo?.row || 0) - (b.logo?.row || 0);
      if (rowDifference) return rowDifference;
      return (a.logo?.position || 0) - (b.logo?.position || 0);
    });

export const getAutomaticLogoLayout = (logoCount) =>
  logoCount > LOGOS_PER_ROW ? "grid" : "row";

const getGridFirstRowCount = (logoCount) => Math.ceil(logoCount / 2);

const positionForIndex = (index, logoCount, layout) => {
  if (layout !== "grid") return { row: 0, position: index };
  const firstRowCount = getGridFirstRowCount(logoCount);
  return index < firstRowCount
    ? { row: 0, position: index }
    : { row: 1, position: index - firstRowCount };
};

export const compactLogoItems = (items = [], requestedLayout = "auto") => {
  const assigned = getAssignedLogoEntries(items).slice(0, MAX_ASSIGNED_LOGOS);
  const layout =
    requestedLayout === "auto"
      ? getAutomaticLogoLayout(assigned.length)
      : requestedLayout;

  return {
    layout,
    items: assigned.map(({ item }, index) => ({
      ...item,
      logo: {
        ...item.logo,
        ...positionForIndex(index, assigned.length, layout),
      },
    })),
  };
};

export const addLogoAssignment = (items = [], asset) => {
  const assigned = getAssignedLogoEntries(items);
  if (!asset || assigned.length >= MAX_ASSIGNED_LOGOS) {
    return {
      items,
      layout: getAutomaticLogoLayout(assigned.length),
      selectedIndex: Math.max(assigned.length - 1, 0),
      added: false,
    };
  }

  const nextItems = [
    ...assigned.map(({ item }) => item),
    {
      logo: {
        ...defaultLogo,
        key: asset.key || "",
        thumbnail: asset.thumbnail || asset.url || "",
        url: asset.url || asset.thumbnail || "",
        title: asset.name || asset.title || "",
        row: Number.MAX_SAFE_INTEGER,
        position: Number.MAX_SAFE_INTEGER,
      },
    },
  ];
  const compacted = compactLogoItems(nextItems);
  return {
    ...compacted,
    selectedIndex: compacted.items.length - 1,
    added: true,
  };
};

export const replaceLogoAssignment = (items = [], selectedIndex, asset) => {
  const assigned = getAssignedLogoEntries(items);
  const target = assigned[selectedIndex];
  if (!target || !asset) return items;

  return items.map((item, itemIndex) =>
    itemIndex === target.itemIndex
      ? {
          ...item,
          logo: {
            ...item.logo,
            key: asset.key || "",
            thumbnail: asset.thumbnail || asset.url || "",
            url: asset.url || asset.thumbnail || "",
            title: asset.name || asset.title || item.logo?.title || "",
          },
        }
      : item,
  );
};

export const updateLogoAssignment = (items = [], selectedIndex, patch) => {
  const assigned = getAssignedLogoEntries(items);
  const target = assigned[selectedIndex];
  if (!target) return items;

  return items.map((item, itemIndex) =>
    itemIndex === target.itemIndex
      ? { ...item, logo: { ...item.logo, ...patch } }
      : item,
  );
};

export const removeLogoAssignment = (items = [], selectedIndex) => {
  const assigned = getAssignedLogoEntries(items);
  if (!assigned[selectedIndex]) {
    return {
      items,
      layout: getAutomaticLogoLayout(assigned.length),
      selectedIndex: Math.max(assigned.length - 1, 0),
    };
  }

  const remaining = assigned
    .filter((_, index) => index !== selectedIndex)
    .map(({ item }) => item);
  const compacted = compactLogoItems(remaining);
  return {
    ...compacted,
    selectedIndex: Math.min(selectedIndex, compacted.items.length - 1),
  };
};

export const moveLogoAssignment = (items = [], selectedIndex, direction) => {
  return reorderLogoAssignment(items, selectedIndex, selectedIndex + direction);
};

export const reorderLogoAssignment = (items = [], selectedIndex, targetIndex) => {
  const assigned = getAssignedLogoEntries(items).map(({ item }) => item);
  if (
    selectedIndex < 0 ||
    selectedIndex >= assigned.length ||
    targetIndex < 0 ||
    targetIndex >= assigned.length
  ) {
    return {
      items,
      layout: getAutomaticLogoLayout(assigned.length),
      selectedIndex,
    };
  }

  const [selectedItem] = assigned.splice(selectedIndex, 1);
  assigned.splice(targetIndex, 0, selectedItem);
  const orderedItems = assigned.map((item, index) => ({
    ...item,
    logo: { ...item.logo, row: 0, position: index },
  }));
  const compacted = compactLogoItems(orderedItems);
  return { ...compacted, selectedIndex: targetIndex };
};

export const getLogoAssetName = (logo, fallback = "Logo") => {
  const rawName =
    logo?.name ||
    logo?.title ||
    logo?.key ||
    logo?.url ||
    logo?.thumbnail ||
    "";
  const encodedName = rawName
    .split("?")[0]
    .split("/")
    .filter(Boolean)
    .pop();
  let cleanName = encodedName;
  try {
    cleanName = decodeURIComponent(encodedName);
  } catch (error) {
    // Keep the original filename when an old asset contains malformed escapes.
  }
  if (!cleanName) return fallback;
  return cleanName.replace(/(\.png|\.jpe?g)\d{10,}$/i, "$1");
};

export const getLogoScalePreset = (scale) =>
  Object.entries(LOGO_SIZE_PRESETS).find(([, value]) => value === scale)?.[0] ||
  "custom";
